import { bearerFetcher, useAuthBearerFetch } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback } from "react"
import { m2mCleaning } from "../endpoints/m2m-cleaning"
import {
    isRetryableUploadError,
    uploadBlobWithPresignedPut
} from "@/utils/presignedUpload"
import { useBackgroundFetchCtx } from "@/hooks/useBackgroundFetch"

export interface Task {
    id: string
    title: string
    description: string
    samplePictureUrl: string
    scope: string
    canSkip: boolean
    order: number
    checkList: string[]
    createdAt: number
}

export const useCleaningTasks = (cleaningId: string) => {
    const { token } = useAuth()
    const { Fetch } = useBackgroundFetchCtx()

    const { data, error } = useAuthBearerFetch<Task[]>(
        token,
        m2mCleaning.cleanerV2.listCleaningTasks({ cleaningId }),
        {}
    )

    // 撮影と同時に呼ぶ: issue + S3 PUT(pending) のみ。complete は確定操作時にまとめて送る。
    // signal で撮影中の × による中断に対応する。
    const requestPutTaskPicture = useCallback(
        async (
            cleaningId: string,
            taskId: string,
            blob: Blob,
            signal?: AbortSignal
        ): Promise<{ uploadId: string }> => {
            const issueUrl = m2mCleaning.cleanerV2.issueTaskPictureUploadUrl({
                cleaningId,
                taskId
            })
            return uploadBlobWithPresignedPut({ token, issueUrl, blob, signal })
        },
        [token]
    )

    // 確定操作（OK）時に呼ぶ: complete のみ。冪等。
    // 失敗はバックグラウンドキューに退避して後で再送する（S3 PUT までは成功済み）。
    const completeTaskPicture = useCallback(
        async (cleaningId: string, taskId: string, uploadId: string) => {
            const completeUrl = m2mCleaning.cleanerV2.completeTaskPictureUpload(
                {
                    cleaningId,
                    taskId
                }
            )
            const body = JSON.stringify({ uploadId })
            try {
                const result = await bearerFetcher(token ?? "", completeUrl, {
                    method: "POST",
                    body
                })
                if (result.error) {
                    throw Object.assign(new Error(`complete failed`), {
                        status: result.error.status
                    })
                }
            } catch (e) {
                if (!isRetryableUploadError(e)) {
                    throw e
                }
                try {
                    await Fetch(completeUrl, {
                        method: "POST",
                        headers: token
                            ? { Authorization: `Bearer ${token}` }
                            : {},
                        body
                    })
                } catch (queueError) {
                    console.error(
                        "[useCleaningTasks] BackgroundFetch enqueue failed",
                        queueError
                    )
                    throw queueError
                }
            }
        },
        [Fetch, token]
    )

    return { data, requestPutTaskPicture, completeTaskPicture, error }
}
